import {
  GetAnalysisDataFrame,
  GetAnalysisPipelineStep,
  GetAnalysisStepData,
} from 'analysis-tool/AnalysisTypes';
import {assert, assertAndContinue} from 'Asserts';

/** @package */
export const MISSING_STEP_DATA_ERROR = 'Expected step to have step data with name';
/** @package */
export const NO_DATA_FRAMES_ERROR = 'Expected step data to have at least one data frame';
/** @package */
export const TOO_MANY_DATA_FRAMES_ERROR = 'Expected step data to only have one data frame';

export function findAndAssertStepData(
  step: GetAnalysisPipelineStep,
  stepDataName: string,
  isLookingForInputStepData: boolean
): GetAnalysisStepData {
  const stepData = findNullableStepData(step, stepDataName, isLookingForInputStepData);

  assert(stepData != null, MISSING_STEP_DATA_ERROR, {isLookingForInputStepData, step, stepDataName});
  return stepData;
}

export function findNullableStepData(
  step: GetAnalysisPipelineStep,
  stepDataName: string,
  isLookingForInputStepData: boolean
): GetAnalysisStepData | null {
  const stepData = isLookingForInputStepData
    ? step.inputStepDatas.find(({name}) => name === stepDataName)
    : step.outputStepDatas.find(({name}) => name === stepDataName);

  return stepData ?? null;
}

export function findAndAssertInputStepData(
  step: GetAnalysisPipelineStep,
  stepDataName: string
): GetAnalysisStepData {
  return findAndAssertStepData(step, stepDataName, true);
}

export function findAndAssertOutputStepData(
  step: GetAnalysisPipelineStep,
  stepDataName: string
): GetAnalysisStepData {
  return findAndAssertStepData(step, stepDataName, false);
}

/**
 * Given a step and a step data name, assert the presence of the step data and its data frames as either input
 * or output step data and return its data frames.
 */
export function findAndAssertStepDataFrames(
  step: GetAnalysisPipelineStep,
  stepDataName: string,
  isLookingForInputStepData: boolean,
  isOptional: boolean = false
): Array<GetAnalysisDataFrame> {
  const stepData = isLookingForInputStepData
    ? step.inputStepDatas.find(({name}) => name === stepDataName)
    : step.outputStepDatas.find(({name}) => name === stepDataName);

  assert(stepData != null, MISSING_STEP_DATA_ERROR, {isLookingForInputStepData, step, stepDataName});
  assert(isOptional || stepData.dataFrames.length >= 1, NO_DATA_FRAMES_ERROR, {
    isLookingForInputStepData,
    step,
    stepDataName,
  });

  return stepData.dataFrames;
}

/**
 * Given a step and a step data name, assert the presence of the step data and its data frames as either input
 * or output step data and return the ids of its data frames.
 *
 * This is useful to get the data frame ids out of a step in order to use them in a GraphQL query to get the
 * step's data frames.
 */
export default function findAndAssertStepDataFrameIds(
  step: GetAnalysisPipelineStep,
  stepDataName: string,
  isLookingForInputStepData: boolean
): Array<string> {
  return findAndAssertStepDataFrames(step, stepDataName, isLookingForInputStepData).map(({id}) => id);
}

/**
 * Given a step and a step data name, assert the presence of the step data as either an input or output step data
 * that should only have one data frame and return the data frame.
 *
 * The assumption of having 1 data frame is checked as a WARNING instead of an ERROR, so that the application
 * doesn't fail with a hard error.
 */
export function findAndAssertSingleStepDataFrame(
  step: GetAnalysisPipelineStep,
  stepDataName: string,
  isLookingForInputStepData: boolean = false
): GetAnalysisDataFrame {
  const dataFrames = findAndAssertStepDataFrames(step, stepDataName, isLookingForInputStepData);
  assertAndContinue(dataFrames.length === 1, TOO_MANY_DATA_FRAMES_ERROR, {
    step,
    stepDataName,
    isLookingForInputStepData,
  });

  return dataFrames[0];
}

/**
 * Given a step and a step data name, assert the presence of the step data as either an input or output step data
 * that should only have one data frame and return the data frame's id.
 *
 * The assumption of having 1 data frame is checked as a WARNING instead of an ERROR, so that the application
 * doesn't fail with a hard error.
 */
export function findAndAssertSingleStepDataFrameId(
  step: GetAnalysisPipelineStep,
  stepDataName: string,
  isLookingForInputStepData: boolean = false
): string {
  const dataFrame = findAndAssertSingleStepDataFrame(step, stepDataName, isLookingForInputStepData);
  return dataFrame.id;
}

export function findSingleStepDataFrameId(
  step: GetAnalysisPipelineStep,
  stepDataName: string,
  isLookingForInputStepData: boolean = false
): string | null {
  const dataFrames = findAndAssertStepDataFrames(step, stepDataName, isLookingForInputStepData, true);

  if (dataFrames.length === 0) {
    return null;
  }

  assertAndContinue(dataFrames.length === 1, TOO_MANY_DATA_FRAMES_ERROR, {
    step,
    stepDataName,
    isLookingForInputStepData,
  });

  return dataFrames[0].id;
}

export function findSingleStepOutputDataFrameId(
  step: GetAnalysisPipelineStep,
  stepDataName: string
): string | null {
  return findSingleStepDataFrameId(step, stepDataName);
}
